import { MARKDOWN_EXTENSIONS } from '@sohanscript/markdown';
import { createHeadingIdFactory } from '@sohanscript/markdown/heading-ids';
import type { BlockNode } from '@tanstack/markdown';
import { parseMarkdown } from '@tanstack/markdown';

export interface TocHeading {
	id: string;
	text: string;
	level: number;
}

const TOC_MAX_LEVEL = 3;

/**
 * Reads headings with the same parser, extensions and slugging the renderer uses,
 * so every dropdown link resolves to a real `id`. A regex scan used to disagree
 * with the renderer on links, code spans, `---` rules inside fences and
 * duplicate headings, which silently broke the anchors.
 */
export function extractHeadings(markdown: string): TocHeading[] {
	const headingIds = createHeadingIdFactory();
	const document = parseMarkdown(markdown, {
		extensions: MARKDOWN_EXTENSIONS,
		frontmatter: false,
		headingIds,
	});
	const headings: TocHeading[] = [];
	// NOTE: `headingIds` runs for every heading the parser builds, including the
	// ones nested inside directives, so nodes are walked recursively to keep the
	// recorded entries paired with the right heading.
	let cursor = 0;
	const walk = (nodes: readonly BlockNode[], listed: boolean): void => {
		for (const node of nodes) {
			if (node.type === 'heading') {
				const entry = headingIds.entries[cursor++];
				if (listed && node.depth <= TOC_MAX_LEVEL) {
					headings.push({
						id: entry?.id ?? node.id ?? '',
						text: entry?.text ?? '',
						level: node.depth,
					});
				}
				continue;
			}
			// NOTE: only these wrap block children, so recursion stays typed.
			switch (node.type) {
				case 'blockquote':
					walk(node.children, listed);
					break;
				case 'list':
					for (const item of node.items) walk(item.children, listed);
					break;
				// NOTE: directives render code, tabs and editors, never prose, and
				// unknown ones render nothing, so their headings never reach the
				// DOM. The entry is still consumed to keep later ids aligned.
				case 'component':
					walk(node.children, false);
					break;
				default:
					break;
			}
		}
	};
	walk(document.children, true);
	return headings;
}
